import { Clock, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { KpiCard } from '@/components/ui/kpi-card';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import { EmployeeFormDialog } from '@/pages/admin/employees/EmployeeFormDialog';
import { useEmployeeProfile } from '@/hooks/employees';
import { JOB_STATUS_TONES, useJobs } from '@/hooks/jobs';
import { formatDay, formatInstant, formatWallTime } from '@/lib/dates';
import { cn } from '@/lib/utils';
import { formatMinutes } from '@shared/overtime';
import type { EmployeeProfile } from '@shared/schemas/employee';
import { Link } from 'react-router';

/*
 * /admin/employees/:id — added 2026-08-06.
 *
 * **The gap this closes is that there was no per-person page at all.** The
 * office had a list of cleaners and an edit dialog. Answering "how has Nina been
 * doing" meant the jobs list filtered by her, then the timesheets screen
 * filtered by her again, and holding the two halves in your head.
 *
 * Three tabs, in the order the questions get asked: who they are, what they have
 * worked, what hours they have put in.
 *
 * **The job history is the ordinary jobs query with an `employeeId`**, not a
 * list built for this page. One answer to "which jobs is this person on" —
 * a second implementation is a second thing to be wrong.
 */

const TABS = ['Overview', 'Job history', 'Time logs'] as const;
type Tab = (typeof TABS)[number];

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const profile = useEmployeeProfile(id);
  const [tab, setTab] = useState<Tab>('Overview');
  const [editing, setEditing] = useState(false);

  if (profile.isError) {
    return (
      <AdminPage title="Employee">
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="That employee could not be loaded"
            onRetry={() => {
              void profile.refetch();
            }}
          />
        </div>
      </AdminPage>
    );
  }

  if (profile.isPending) {
    return (
      <AdminPage title="Employee">
        <div aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading the employee</span>
          <div aria-hidden="true" className="h-32 animate-pulse rounded-lg bg-white" />
          <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-white" />
        </div>
      </AdminPage>
    );
  }

  const { employee, stats, openShiftSince } = profile.data;

  return (
    <AdminPage
      title={employee.fullName}
      record={employee.fullName}
      description={employee.positionName ?? undefined}
      actions={
        <Button
          variant="outline"
          onClick={() => {
            setEditing(true);
          }}
        >
          <Pencil aria-hidden="true" />
          Edit employee
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {/*
          **On the clock right now, and it leads.** "Where is everyone" is the
          question the office asks most, and the answer being on the person's own
          page is what makes this screen worth opening at 9am.
        */}
        {openShiftSince === null ? null : (
          <span className="bg-status-progress-bg text-status-progress-text inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-meta font-medium">
            <Clock aria-hidden="true" className="size-3.5" />
            On the clock since {formatWallTime(openShiftSince.slice(11, 16))}
          </span>
        )}
        {employee.isActive ? null : <StatusBadge label="Inactive" tone="cancelled" />}
      </div>

      <dl className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Zero is `0` — a new starter who has not been on a job is a fact, not
            a dash (P1, P10). */}
        <KpiCard label="Jobs worked" value={stats.jobsTotal} subLabel="Ever on the crew" />
        <KpiCard label="Completed" value={stats.jobsCompleted} subLabel="Of those, finished" />
        <KpiCard
          label="Hours clocked"
          value={stats.minutesClocked}
          format={formatMinutes}
          subLabel="Closed shifts, all time"
        />
        <KpiCard label="Shifts" value={stats.shifts} subLabel="Closed shifts, all time" />
      </dl>

      <nav aria-label="Employee sections" className="mb-4 flex flex-wrap gap-2" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry}
            type="button"
            role="tab"
            aria-selected={tab === entry}
            onClick={() => {
              setTab(entry);
            }}
            className={cn(
              'inline-flex min-h-touch items-center rounded-full px-3.5 text-label',
              tab === entry
                ? 'bg-honey-400 text-ink-900'
                : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
            )}
          >
            {entry}
          </button>
        ))}
      </nav>

      {tab === 'Overview' ? <Overview profile={profile.data} /> : null}
      {tab === 'Job history' ? <JobHistory employeeId={employee.id} /> : null}
      {tab === 'Time logs' ? <TimeLogs profile={profile.data} /> : null}

      <EmployeeFormDialog open={editing} onOpenChange={setEditing} employee={employee} />
    </AdminPage>
  );
}

function Overview({ profile }: { profile: EmployeeProfile }) {
  const { employee } = profile;

  return (
    <div className="space-y-4">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Phone">
          {employee.phone ?? <span className="text-ink-500">Not recorded</span>}
        </Fact>
        <Fact label="Signs in with">
          {/* A cleaner hired without a work address signs in with a username,
              and saying so beats a blank — the same call the Topbar makes. */}
          {employee.email ?? employee.username ?? <span className="text-ink-500">No login</span>}
        </Fact>
        <Fact label="Hired">
          {employee.hireDate ? (
            formatDay(employee.hireDate)
          ) : (
            <span className="text-ink-500">Not recorded</span>
          )}
        </Fact>
        <Fact label="Position">
          {employee.positionName ?? <span className="text-ink-500">Not set</span>}
        </Fact>
        <Fact label="Jobs a day" hint="Auto-assignment will not go past this.">
          {employee.maxJobsPerDay}
        </Fact>
        <Fact label="Last signed in">
          {employee.lastLoginAt ? (
            formatInstant(employee.lastLoginAt)
          ) : (
            <span className="text-ink-500">Never</span>
          )}
        </Fact>
      </dl>

      <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
        <h2 className="text-h3 text-ink-900">Skills</h2>
        {employee.skills.length === 0 ? (
          <p className="mt-2 text-meta text-ink-500">
            None recorded. Auto-assignment scores a skill match, so a cleaner with none set will
            rank below one who has them.
          </p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-2">
            {employee.skills.map((entry) => (
              <li
                key={entry.id}
                className="rounded-full bg-ink-900/5 px-2.5 py-0.5 text-meta text-ink-700"
              >
                {entry.name}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/*
        **The home address and the pay rate are on the edit dialog, not here.**
        Both are admin-only either way, but a profile page is opened casually and
        often with somebody standing behind you — a screen that puts a colleague's
        home address and hourly rate on it by default is one people learn not to
        open in front of anyone.
      */}
      <p className="text-meta text-ink-500">Home address and pay rate are on Edit employee.</p>
    </div>
  );
}

/**
 * Their jobs — the ordinary jobs query, scoped to this person.
 *
 * `useJobs` with an `employeeId` is exactly what `/admin/jobs` runs, so this
 * cannot drift from the jobs screen. It is paginated for the same reason that
 * one is: a cleaner two years in has hundreds.
 */
function JobHistory({ employeeId }: { employeeId: string }) {
  const [page, setPage] = useState(1);
  const jobs = useJobs({ sort: 'newest', page, search: '', status: 'all', employeeId });

  if (jobs.isError) {
    return (
      <div className="rounded-lg border border-ink-300 bg-white shadow-card">
        <ErrorState
          title="Their jobs could not be loaded"
          onRetry={() => {
            void jobs.refetch();
          }}
        />
      </div>
    );
  }

  if (jobs.isPending) {
    return <div aria-busy="true" className="h-64 animate-pulse rounded-lg bg-white" />;
  }

  if (jobs.data.data.length === 0) {
    return (
      <div className="rounded-lg border border-ink-300 bg-white shadow-card">
        <EmptyState
          title="They have not been on a job yet."
          hint="Assign them from a job, or from the calendar."
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ol className="space-y-2">
        {jobs.data.data.map((entry) => (
          <li key={entry.id}>
            <Link
              to={`/admin/jobs/${entry.id}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-ink-300 bg-white p-3 shadow-card hover:bg-honey-50"
            >
              <span className="text-label text-ink-900">{entry.jobNumber}</span>
              <span className="text-body text-ink-700">{entry.clientName}</span>
              {/* The service type's name, never the stored key — P5. */}
              <span className="text-meta text-ink-500">{entry.serviceTypeName}</span>
              <span className="text-meta text-ink-500">{formatDay(entry.scheduledDate)}</span>
              <span className="ml-auto">
                <StatusBadge label={entry.statusLabel} tone={JOB_STATUS_TONES[entry.status]} />
              </span>
            </Link>
          </li>
        ))}
      </ol>

      <Pagination
        page={jobs.data.page}
        pageSize={jobs.data.pageSize}
        total={jobs.data.total}
        onPageChange={setPage}
        itemNoun="jobs"
      />
    </div>
  );
}

/**
 * The last thirty days of shifts.
 *
 * **Not the payroll document.** That is `/admin/timesheets`, which takes a
 * period, splits regular from overtime and exports a CSV. This is the "has
 * anything looked odd lately" view, and saying so stops somebody using it to
 * work out what to pay.
 */
function TimeLogs({ profile }: { profile: EmployeeProfile }) {
  if (profile.recentShifts.length === 0) {
    return (
      <div className="rounded-lg border border-ink-300 bg-white shadow-card">
        <EmptyState
          title="No shifts in the last thirty days."
          hint="Cleaners clock in and out from their own portal."
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ol className="space-y-2">
        {profile.recentShifts.map((shift) => (
          <li
            key={shift.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-ink-300 bg-white p-3 shadow-card"
          >
            <span className="text-label text-ink-900">
              {formatDay(shift.clockInAt.slice(0, 10))}
            </span>
            <span className="text-body text-ink-700">
              {formatInstant(shift.clockInAt)} –{' '}
              {shift.clockOutAt === null ? (
                <span className="text-status-progress-text">still on the clock</span>
              ) : (
                formatInstant(shift.clockOutAt)
              )}
            </span>
            {shift.jobNumber === null ? null : (
              <span className="text-meta text-ink-500">{shift.jobNumber}</span>
            )}
            {/* An admin has moved this one. The screen says so rather than
                showing a corrected figure as if it were clocked — 10.3. */}
            {shift.wasCorrected ? (
              <span className="rounded-full bg-ink-900/5 px-2 py-0.5 text-meta text-ink-700">
                Corrected
              </span>
            ) : null}
            <span className="ml-auto text-body tabular-nums text-ink-900">
              {shift.durationMinutes === null ? '—' : formatMinutes(shift.durationMinutes)}
            </span>
          </li>
        ))}
      </ol>

      <p className="text-meta text-ink-500">
        The last thirty days. Timesheets is where a period is totalled and exported.
      </p>
    </div>
  );
}

function Fact({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <dt className="text-meta text-ink-500">{label}</dt>
      <dd className="mt-1 text-h3 text-ink-900">{children}</dd>
      {hint ? <p className="mt-1 text-meta text-ink-500">{hint}</p> : null}
    </div>
  );
}
